import onFolderDropdownOptionSelected from '../../actions/onFolderDropdownOptionSelected';
import { getStore } from '../../store/store';
import getSearchScopeDisplayName from '../../utils/getSearchScopeDisplayName';
import { observer } from 'mobx-react';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { logUsage } from 'owa-analytics';
import folderStore from 'owa-folders';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import loc, { format } from 'owa-localize';
import { SearchScenarioId } from 'owa-search-next';
import { compareSearchScope, getKey, SearchScope, SearchScopeKind } from 'owa-search-service';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import * as React from 'react';
import {
    advancedSearchFoldersLabelNext,
    searchScopesAriaLabelNext,
} from './FoldersDropdown.locstring.json';
import {
    Dropdown,
    DropdownMenuItemType,
    IDropdownOption,
    IDropdown,
} from 'office-ui-fabric-react/lib/Dropdown';

const styles = require('../AdvancedSearch.scss');

export interface FoldersDropdownProps {
    useLeftLabel: boolean;
    onFocusChange: (isFowardNavigation: boolean) => void;
}

@observer
export default class FoldersDropdown extends React.Component<FoldersDropdownProps, {}> {
    private dropdown: IDropdown;

    public focusElement() {
        this.dropdown.focus();
    }

    private refCallback = (dropdown: IDropdown) => {
        this.dropdown = dropdown;
    };

    render() {
        const { useLeftLabel } = this.props;

        const fieldContainerLabel = useLeftLabel
            ? styles.formFieldContainerLeft
            : styles.formFieldContainerAbove;
        const selectedSearchScope = this.getSelectedOption();

        return (
            <div className={fieldContainerLabel} onKeyDown={this.onKeyDown}>
                <Label
                    styles={{ root: styles.formFieldLabel }}
                    title={loc(advancedSearchFoldersLabelNext)}>
                    {loc(advancedSearchFoldersLabelNext)}
                </Label>
                <Dropdown
                    ariaLabel={format(
                        loc(searchScopesAriaLabelNext),
                        getSearchScopeDisplayName(selectedSearchScope)
                    )}
                    className={styles.foldersDropdown}
                    onChange={this.onChange}
                    options={this.getFolderDropdownOptions()}
                    selectedKey={getKey(selectedSearchScope)}
                    styles={{
                        dropdownItemsWrapper: styles.foldersDropdownItemsWrapper,
                        title: styles.foldersDropdownTitle,
                        root: styles.foldersDropdownRoot,
                    }}
                    componentRef={this.refCallback}
                />
            </div>
        );
    }

    private onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        if (evt.shiftKey && evt.keyCode === KeyboardCharCodes.Tab) {
            evt.preventDefault();
            evt.stopPropagation();
            this.props.onFocusChange(false);
        }
    };

    /**
     * Determines the selected option in the dropdown. If the user hasn't yet
     * selected an option, the default selected option will be used. Otherwise,
     * whatever the user manually selected will be respected.
     */
    private getSelectedOption(): SearchScope {
        const { staticSearchScope: selectedSearchScope, advancedSearchViewState } = getStore();
        return advancedSearchViewState.selectedSearchScope || selectedSearchScope;
    }

    /**
     * Gets the corresponding search scope based on the key of the selected
     * dropdown option and dispatches an action to update the store.
     *
     * @param event The onChange event (unused)
     * @param option The option that was selected by the user
     */
    private onChange(event: any, option: IDropdownOption) {
        const {
            staticSearchScopeList: searchScopes,
            staticSearchScope: previousScope,
        } = getStore();
        const { key } = option;

        const selectedScope = searchScopes.filter((scope: SearchScope) => {
            return getKey(scope) === key;
        })[0];

        // Log folder scope change (if within primary or archive mailbox).
        if (
            (previousScope.kind === SearchScopeKind.PrimaryMailbox &&
                selectedScope.kind === SearchScopeKind.PrimaryMailbox) ||
            (previousScope.kind === SearchScopeKind.ArchiveMailbox &&
                selectedScope.kind === SearchScopeKind.ArchiveMailbox)
        ) {
            logUsage('Search_AdvancedSearchScopeChanged', [
                folderIdToName(previousScope.folderId),
                folderIdToName(selectedScope.folderId),
                selectedScope.kind,
                SearchScenarioId.Mail,
            ]);
        }

        onFolderDropdownOptionSelected({
            key: getKey(selectedScope),
            text: getSearchScopeDisplayName(selectedScope),
            searchScope: selectedScope,
        });
    }

    /**
     * Returns array of dropdown options for folders dropdown.
     */
    private getFolderDropdownOptions(): IDropdownOption[] {
        const { staticSearchScopeList: searchScopes, initialSearchScope } = getStore();
        const dropdownItems: IDropdownOption[] = [];

        // Add the initial folder scope (if not there already).
        const initialSearchScopePresent =
            searchScopes.filter(searchScope => compareSearchScope(searchScope, initialSearchScope))
                .length === 1;

        if (!initialSearchScopePresent) {
            dropdownItems.push({
                key: getKey(initialSearchScope),
                text: getSearchScopeDisplayName(initialSearchScope),
            });
        }

        // Add distinguished folder items first.
        dropdownItems.push(...this.getFolderOptions(true /* isDistinguished */));

        // Add divider (if any distinguished folders were added).
        if (dropdownItems.length > 0) {
            dropdownItems.push({
                key: 'foldersDropdownDivider',
                text: '-',
                itemType: DropdownMenuItemType.Divider,
            });
        }

        // Add user folder items.
        dropdownItems.push(...this.getFolderOptions(false /* isDistinguished */));

        return dropdownItems;
    }

    /**
     * Helper function to get folders (distinguished or not) and convert them
     * to an array of IDropdownOption objects to get rendered.
     *
     * @param isDistinguished Indicates whether or not distinguished folders should
     * be returned.
     */
    private getFolderOptions(isDistinguished: boolean): IDropdownOption[] {
        const { staticSearchScopeList: searchScopes } = getStore();
        const dropdownItems: IDropdownOption[] = [];

        searchScopes
            .filter((scope: SearchScope) => {
                switch (scope.kind) {
                    case SearchScopeKind.PrimaryMailbox:
                        return (
                            !!folderStore.folderTable.get(scope.folderId).DistinguishedFolderId ===
                            isDistinguished
                        );
                    default:
                        return !isDistinguished;
                }
            })
            .map((scope: SearchScope) => {
                dropdownItems.push({
                    key: getKey(scope),
                    text: getSearchScopeDisplayName(scope),
                });
            });

        return dropdownItems;
    }
}
